import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getHelpByRoute, getHelpTextToSpeech } from '../../settings/HelpConfigMap';

const HelpContext = createContext();

export const useHelp = () => {
    const context = useContext(HelpContext);
    if (!context) {
        throw new Error('useHelp debe ser usado dentro de HelpProvider');
    }
    return context;
};

export const HelpProvider = ({ children }) => {
    const location = useLocation();
    const [helpConfig, setHelpConfig] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const utteranceRef = useRef(null);

    useEffect(() => {
        const pathname = location.pathname;
        const config = getHelpByRoute(pathname);
        setHelpConfig(config);
        
        if (isOpen) {
            stopSpeech();
        }
    }, [location.pathname]);

    const toggleHelpPanel = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const openHelpPanel = useCallback(() => {
        setIsOpen(true);
    }, []);

    const closeHelpPanel = useCallback(() => {
        setIsOpen(false);
        stopSpeech();
    }, []);

    const stopSpeech = useCallback(() => {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    }, []);

    const speakHelp = useCallback((section = 'all') => {
        if (!helpConfig || !window.speechSynthesis) return;

        stopSpeech();
        
        const textToSpeech = getHelpTextToSpeech(location.pathname, section);
        
        const utterance = new SpeechSynthesisUtterance(textToSpeech);
        utterance.lang = 'es-MX';
        utterance.rate = 0.9;
        utterance.pitch = 1;
        
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    }, [helpConfig, location.pathname, stopSpeech]);

    const value = {
        helpConfig,
        isOpen,
        isSpeaking,
        toggleHelpPanel,
        openHelpPanel,
        closeHelpPanel,
        speakHelp,
        stopSpeech
    };

    return (
        <HelpContext.Provider value={value}>
            {children}
        </HelpContext.Provider>
    );
};

export default HelpContext;